import { standaloneChatWindow, remoteChatUrl, navigateToChat } from './guide-chat-navigation';
import { useLiveChannel } from '../hooks/useLiveChannel';
import { useCallback, useEffect, useRef, useState } from 'react';
import CuiLitePanel from './panels/CuiLitePanel';
import BrowserPanel from './panels/BrowserPanel';
import { aktuellerBerater, type BuehneAnsicht } from './GuideHof';

export type GuideBeraterStart = { buehne: BuehneAnsicht; hierHaus?: 'dev' | 'partner' | null };

/** Navigate only the dedicated conversation window, avoiding an insecure Dev
 * websocket inside a Partner HTTPS iframe. No message or session-start action. */
function StandaloneChatRedirect({ address }: { address: string }) {
  useEffect(() => { navigateToChat(address); }, [address]);
  return <div role="status">Gespräch wird im zuständigen Haus geöffnet …</div>;
}

/** Resolve the stage on its owning server; never open a foreign session locally.
 *
 * Zwei Wege, die hier zusammenlaufen (Merge 15.09.2026, codex/guide-durable-rollout in develop):
 * - `start` (Durable Rollout): die schon geladene Bühne des eigenen Hauses wird als Vorlade-Ziel
 *   genutzt, und der Live-Kanal löst bei `guide-updated` eine Neu-Auflösung aus.
 * - `sitzung` (Bühnenhistorie): ein historisches Gespräch wird gezielt geöffnet; dafür gilt das
 *   Vorlade-Ziel nicht, weil `aktuellerBerater` nur den aktuellen Berater kennt. */
export default function GuideBeraterChat({ buehneId, sitzung, visible = true, start }: { buehneId: string; sitzung?: string; visible?: boolean; start?: GuideBeraterStart }) {
  const vorhandenesZiel = !sitzung && start?.buehne.id === buehneId && start.hierHaus === start.buehne.haus && aktuellerBerater(start.buehne)?.offen ? { lokal: true, buehne: start.buehne } : null;
  const zuordnung = JSON.stringify([buehneId, sitzung, start?.hierHaus, start?.buehne.haus, start?.buehne.sitzungen, start?.buehne.berater?.map(b => [b.sitzung, b.konto, b.offen])]);
  const [ziel, setZiel] = useState<{ lokal: boolean; buehne: BuehneAnsicht; webviewUrl?: string } | null>(vorhandenesZiel);
  const [fehler, setFehler] = useState<string | null>(null);
  const [versuch, setVersuch] = useState(0);
  const [chatVersuch, setChatVersuch] = useState(0);
  const [chatFehler, setChatFehler] = useState<string | null>(null);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const chatGeladen = useCallback(() => { setChatFehler(null); setInitialLoaded(true); }, []);
  const chatFehlgeschlagen = useCallback((_sid: string) => setChatFehler(alt => alt ?? 'Der Chat lädt noch nicht. Die Verbindung wird weiter geprüft.'), []);
  const chatLadefehler = useCallback((grund: string) => setChatFehler(grund), []);
  useEffect(() => {
    setZiel(vorhandenesZiel); setFehler(null); setChatFehler(null); setInitialLoaded(false);
  }, [buehneId]);
  useEffect(() => {
    setZiel(vorhandenesZiel); setChatFehler(null); setInitialLoaded(false);
  }, [zuordnung]);
  // A failed one-use login hop needs a fresh server-resolved URL, never replay.
  const frischesTicket = useRef(false);
  const ticketVersuche = useRef(0);
  const ticketTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const fernChatFehler = useCallback(() => {
    if (ticketVersuche.current >= 3 || ticketTimer.current !== undefined) return false;
    const abstand = [5000, 10000, 30000][ticketVersuche.current++];
    ticketTimer.current = setTimeout(() => {
      ticketTimer.current = undefined;
      frischesTicket.current = true;
      setVersuch(v => v + 1);
    }, abstand);
    return true;
  }, []);
  const fernNavigationStart = useCallback(() => {
    clearTimeout(ticketTimer.current);
    ticketTimer.current = undefined;
    frischesTicket.current = false;
  }, []);
  const fernChatGeladen = useCallback(() => {
    ticketVersuche.current = 0;
    clearTimeout(ticketTimer.current);
    ticketTimer.current = undefined;
  }, []);
  useEffect(() => {
    ticketVersuche.current = 0;
    frischesTicket.current = false;
    return () => { clearTimeout(ticketTimer.current); ticketTimer.current = undefined; };
  }, [buehneId, sitzung]);
  const resolverLaeuft = useRef(false);
  const updateOffen = useRef(false);
  const updateTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const aktualisieren = useCallback(() => {
    if (resolverLaeuft.current) { updateOffen.current = true; return; }
    clearTimeout(updateTimer.current);
    updateTimer.current = setTimeout(() => setVersuch(v => v + 1), 250);
  }, []);
  useLiveChannel({ label: 'Berater-Zuordnung', onResync: aktualisieren, onMessage: (m: any) => {
    if (m?.type === 'guide-updated' && (!m.buehneId || m.buehneId === buehneId)) aktualisieren();
  } });
  useEffect(() => () => clearTimeout(updateTimer.current), []);
  useEffect(() => {
    let weg = false;
    let wiederholen = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const controller = new AbortController();
    resolverLaeuft.current = true;
    setFehler(null);
    fetch(`/api/guide/coach/berater-ziel/${encodeURIComponent(buehneId)}`, { signal: AbortSignal.any([controller.signal, AbortSignal.timeout(15000)]) })
      .then(async r => { const d = await r.json(); if (!r.ok) { if ([401,403,404].includes(r.status)) { wiederholen = false; if (!weg) setZiel(null); } throw new Error(d.error || `HTTP ${r.status}`); } return d; })
      .then(d => {
        if (weg) return;
        const erneuern = frischesTicket.current;
        frischesTicket.current = false;
        setZiel(alt => !erneuern && alt && !alt.lokal && !d.lokal && alt.buehne.haus === d.buehne.haus ? alt : d);
      })
      .catch(e => {
        if (weg) return;
        setFehler(e.name === 'TimeoutError' ? 'Die Verbindung zum Berater braucht gerade länger. Wir versuchen es erneut.' : e.message);
        if (wiederholen) timer = setTimeout(() => setVersuch(v => v + 1),
          ziel ? 15000 : Math.min(1000 * 2 ** Math.min(versuch, 3), 5000));
      }).finally(() => {
        if (!weg) { resolverLaeuft.current = false; if (updateOffen.current) { updateOffen.current = false; aktualisieren(); } }
      });
    return () => { weg = true; resolverLaeuft.current = false; clearTimeout(timer); controller.abort(); };
  }, [buehneId, versuch, zuordnung]);
  if (fehler && !ziel) return <div role="alert">{fehler} <button onClick={() => setVersuch(v => v + 1)}>Erneut laden</button></div>;
  if (!ziel) return <div role="status">Berater-Chat wird geladen …</div>;
  const remoteUrl = ziel.webviewUrl ? remoteChatUrl(ziel.webviewUrl, window.location.href, sitzung) : null;
  if (!ziel.lokal && remoteUrl && standaloneChatWindow(window.location.href, window.top === window)) {
    return <StandaloneChatRedirect address={remoteUrl} />;
  }
  if (!ziel.lokal) return remoteUrl
    ? (visible ? <BrowserPanel key={`${buehneId}:${ziel.buehne.haus}:${sitzung ?? ''}:${remoteUrl}`} initialUrl={remoteUrl} cuiChatGatewayRecovery onLadeFehler={fernChatFehler} onGeladen={fernChatGeladen} onNavigationStart={fernNavigationStart} /> : null)
    : <div role="alert">Die Adresse des Berater-Chats fehlt.</div>;
  const berater = sitzung ? ziel.buehne.berater?.find(b => b.sitzung === sitzung && ziel.buehne.sitzungen.includes(sitzung)) : aktuellerBerater(ziel.buehne);
  if (!berater?.konto || (!sitzung && !berater.offen)) return <div role="alert">Die Zuordnung dieses Chats ist gerade nicht verfügbar. Seine Kennung bleibt erhalten: {sitzung ?? ziel.buehne.sitzungen.at(-1)}. <button onClick={() => setVersuch(v => v + 1)}>Zuordnung neu laden</button></div>;
  return <>
    {fehler && <div role="status">Letzter geladener Berater · Zuordnung derzeit nicht erneut prüfbar. <button onClick={() => setVersuch(v => v + 1)}>Erneut prüfen</button></div>}
    {chatFehler && <div role="alert">{chatFehler} <button onClick={() => setChatVersuch(v => v + 1)}>Erneut laden</button></div>}
    <CuiLitePanel key={berater.sitzung} panelId={`guide-berater-${buehneId}`}
    buehneId={buehneId} accountId={berater.konto} initialSessionId={berater.sitzung} refreshToken={chatVersuch} isTabVisible={visible || !initialLoaded}
    onLoadFailed={chatFehlgeschlagen} onLoadSucceeded={chatGeladen} onLoadError={chatLadefehler} />
  </>;
}
